// src/Pages/ProfilePage/ProfilePage.js
import React, { useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import api, { refreshRolesJwt } from "../../services/api";
import RoleSelectModal, { CURRENT_APP_ACTION_CODE } from "./RoleSelectModal";
import styles from "./ProfilePage.module.scss";

const KNOWN_ROLE_CODES = [
    "ROLE_USER",
    "ROLE_CLIENT",
    "ROLE_STAFF",
    "ROLE_MANAGER",
    "ROLE_FR",
    "ROLE_FRANCHISEE",
    "ROLE_YK",
    "ROLE_SPECIALIST",
    "ROLE_SPECIALIST_TS",
    "ROLE_OKK",
    "ROLE_DESIGN",
    "ROLE_SUPPORT",
    "ROLE_ADMIN",
    "ROLE_SUPER_ADMIN",
    "ROLE_EXTERNAL_CASHBOX",
];

const CLIENT_ROLE_CODES = ["ROLE_USER", "ROLE_CLIENT"];
const STAFF_ROLE_CODES = ["ROLE_STAFF"];
const LKFR_ROLE_CODES = ["ROLE_FR", "ROLE_FRANCHISEE"];
const CURRENT_APP_ROLE_CODES = [
    "ROLE_SPECIALIST",
    "ROLE_SPECIALIST_TS",
    "ROLE_OKK",
    "ROLE_DESIGN",
    "ROLE_SUPPORT",
    "ROLE_ADMIN",
    "ROLE_SUPER_ADMIN",
    "ROLE_MANAGER",
    "ROLE_YK",
    "ROLE_EXTERNAL_CASHBOX",
];
const CURRENT_APP_STAY_LABEL = "Остаться в support";
const IDP_ORIGIN = process.env.REACT_APP_IDP_ORIGIN || "";

function getRoleDisplayName(roleCode) {
    const roleMap = {
        ROLE_USER: "Клиент",
        ROLE_CLIENT: "Клиент",
        ROLE_STAFF: "Мастер",
        ROLE_MANAGER: "Менеджер",
        ROLE_FR: "Франчайзи",
        ROLE_FRANCHISEE: "Франчайзи",
        ROLE_YK: "УК",
        ROLE_SPECIALIST: "Специалист",
        ROLE_SPECIALIST_TS: "Сотрудник Тех. Поддержки",
        ROLE_OKK: "ОКК",
        ROLE_DESIGN: "Дизайнер",
        ROLE_SUPPORT: "Поддержка",
        ROLE_ADMIN: "Администратор",
        ROLE_SUPER_ADMIN: "Супер администратор",
        ROLE_EXTERNAL_CASHBOX: "Внешняя касса",
    };

    return roleMap[roleCode] || roleCode;
}

function buildRoleList(rolesFromApi) {
    const normalizedRoles = Array.isArray(rolesFromApi)
        ? Array.from(new Set(rolesFromApi.map((role) => String(role))))
        : [];

    const knownRoles = KNOWN_ROLE_CODES.map((code) => ({
        code,
        name: getRoleDisplayName(code),
        hasRole: normalizedRoles.includes(code),
    }));

    const unknownRoles = normalizedRoles
        .filter((code) => !KNOWN_ROLE_CODES.includes(code))
        .map((code) => ({
            code,
            name: getRoleDisplayName(code),
            hasRole: true,
        }));

    return [...knownRoles, ...unknownRoles];
}

function trimTrailingSlash(value) {
    return String(value || "").trim().replace(/\/$/, "");
}

function getFallbackTargets() {
    if (typeof window === "undefined") {
        return {
            client: "/",
            staff: "/",
            lkfr: "/",
            support: "/",
        };
    }

    const protocol = window.location.protocol === "http:" ? "http:" : "https:";
    const host = window.location.hostname.toLowerCase();
    const currentOrigin = trimTrailingSlash(window.location.origin) || "/";

    if (host.endsWith(".devchio.ru")) {
        return {
            client: `${protocol}//widget.devchio.ru`,
            staff: `${protocol}//staff.devchio.ru`,
            lkfr: `${protocol}//lkfr.devchio.ru`,
            support: `${protocol}//support.devchio.ru`,
        };
    }

    if (host.endsWith(".chio-chio.ru")) {
        return {
            client: `${protocol}//widget2.chio-chio.ru`,
            staff: `${protocol}//staff.chio-chio.ru`,
            lkfr: `${protocol}//lkfr.chio-chio.ru`,
            support: `${protocol}//designfront.chio-chio.ru`,
        };
    }

    return {
        client: currentOrigin,
        staff: currentOrigin,
        lkfr: currentOrigin,
        support: currentOrigin,
    };
}

function getAppTargets() {
    const fallbackTargets = getFallbackTargets();

    return {
        client:
            trimTrailingSlash(process.env.REACT_APP_CLIENT_HOME_URL || process.env.REACT_APP_CLIENT_APP_URL) ||
            fallbackTargets.client,
        staff: trimTrailingSlash(process.env.REACT_APP_STAFF_HOME_URL) || fallbackTargets.staff,
        lkfr: trimTrailingSlash(process.env.REACT_APP_LKFR_HOME_URL) || fallbackTargets.lkfr,
        support: trimTrailingSlash(process.env.REACT_APP_SUPPORT_HOME_URL) || fallbackTargets.support,
    };
}

function resolveRoleTarget(roleCode, appTargets, managerWorkspace) {
    if (roleCode === "ROLE_MANAGER" || (roleCode === CURRENT_APP_ACTION_CODE && managerWorkspace)) return new URL('/manager/tickets',appTargets.support).toString();
    if (roleCode === CURRENT_APP_ACTION_CODE) return appTargets.support;
    if (CLIENT_ROLE_CODES.includes(roleCode)) return appTargets.client;
    if (STAFF_ROLE_CODES.includes(roleCode)) return appTargets.staff;
    if (LKFR_ROLE_CODES.includes(roleCode)) return appTargets.lkfr;
    if (CURRENT_APP_ROLE_CODES.includes(roleCode)) return appTargets.support;
    return appTargets.client;
}

const ProfilePage = () => {
    const managerWorkspace=useLocation().pathname.startsWith('/manager/');
    const currentRoleCodes=useMemo(()=>managerWorkspace?['ROLE_MANAGER']:CURRENT_APP_ROLE_CODES.filter(role=>role!=='ROLE_MANAGER'),[managerWorkspace]);
    const [userRoles, setUserRoles] = useState([]);
    const [rolesLoading, setRolesLoading] = useState(false);
    const [rolesRefreshing, setRolesRefreshing] = useState(false);
    const [rolesError, setRolesError] = useState("");
    const [isRoleModalOpen, setRoleModalOpen] = useState(false);

    const appTargets = useMemo(() => getAppTargets(), []);

    useEffect(() => {
        let alive = true;

        const fetchUserRoles = async () => {
            try {
                setRolesLoading(true);
                setRolesError("");

                const response = await api.get(`${IDP_ORIGIN}/api/roles`, {
                    headers: { "Content-Type": "application/json" },
                    withCredentials: true,
                });

                if (!alive) return;

                const rolesFromApi = Array.isArray(response?.data?.roles) ? response.data.roles : [];
                setUserRoles(buildRoleList(rolesFromApi));
            } catch (error) {
                console.error("fetchUserRoles error:", error);

                if (!alive) return;

                setRolesError("Не удалось загрузить роли.");
                setUserRoles(buildRoleList([]));
            } finally {
                if (alive) setRolesLoading(false);
            }
        };

        fetchUserRoles();

        return () => {
            alive = false;
        };
    }, []);

    const availableRoles = useMemo(
        () => userRoles.filter((role) => role.hasRole && !currentRoleCodes.includes(role.code)),
        [userRoles,currentRoleCodes]
    );

    const canSwitchRole = availableRoles.length > 0;

    const handleRoleSelect = (roleCode) => {
        setRoleModalOpen(false);
        window.location.replace(resolveRoleTarget(roleCode, appTargets, managerWorkspace));
    };

    const handleRefreshRoles = async () => {
        try {
            setRolesRefreshing(true);
            setRolesError("");

            const response = await refreshRolesJwt();
            const rolesFromApi = Array.isArray(response?.roles) ? response.roles : [];
            setUserRoles(buildRoleList(rolesFromApi));
            setRoleModalOpen(false);
        } catch (error) {
            console.error("refreshRolesJwt error:", error);
            setRolesError("Не удалось обновить роли.");
        } finally {
            setRolesRefreshing(false);
        }
    };

    return (
        <div className={styles.wrap}>
            <div className={styles.card}>
                <p className={styles.title}>Смена роли</p>
                <p className={styles.subtitle}>
                    {rolesLoading
                        ? "Загружаем доступные приложения..."
                        : canSwitchRole
                            ? `Доступно ролей для перехода: ${availableRoles.length}`
                            : "Дополнительных ролей для перехода не найдено."}
                </p>

                {rolesError && <p className={styles.error}>{rolesError}</p>}

                <div className={styles.actions}>
                    {canSwitchRole && (
                        <button
                            className={styles.btn}
                            onClick={() => setRoleModalOpen(true)}
                            disabled={rolesLoading || rolesRefreshing}
                        >
                            {rolesLoading ? "Загрузка ролей..." : `Сменить роль (${availableRoles.length} доступно)`}
                        </button>
                    )}

                    <button
                        className={`${styles.btn} ${styles.secondaryBtn}`}
                        onClick={handleRefreshRoles}
                        disabled={rolesLoading || rolesRefreshing}
                    >
                        {rolesRefreshing ? "Обновляем роли..." : "Обновить роли"}
                    </button>
                </div>
            </div>

            <RoleSelectModal
                open={isRoleModalOpen && canSwitchRole}
                onClose={() => setRoleModalOpen(false)}
                onSelect={handleRoleSelect}
                availableRoles={availableRoles}
                currentAppRoleCodes={currentRoleCodes}
                currentAppStayLabel={managerWorkspace?'Остаться как менеджер':CURRENT_APP_STAY_LABEL}
            />
        </div>
    );
};

export default ProfilePage;
